import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Calculator,
  Lightbulb,
  BarChart2,
  ArrowLeft,
} from "lucide-react";
import DailyLog from "./DailyLog";
import QuickCalculator from "./QuickCalculator";
import EcoTips from "./EcoTips";
import ProgressView from "./ProgressView";
import { supabase } from "../../integrations/supabase/client";

interface CarbonDashboardProps {
  open: boolean;
  onClose: () => void;
}

type Feature = "log" | "calculator" | "tips" | "progress" | null;

const featureCards = [
  {
    key: "log",
    title: "Daily/Weekly Log",
    description: "Track your transportation, energy, and food choices.",
    icon: <BookOpen className="w-8 h-8 text-[#40e0d0] mb-2" />,
  },
  {
    key: "calculator",
    title: "Quick Calculator",
    description: "Estimate your carbon impact for trips or events.",
    icon: <Calculator className="w-8 h-8 text-[#40e0d0] mb-2" />,
  },
  {
    key: "tips",
    title: "Eco Tips & Challenges",
    description: "Get actionable tips and weekly eco-challenges.",
    icon: <Lightbulb className="w-8 h-8 text-[#40e0d0] mb-2" />,
  },
  {
    key: "progress",
    title: "Progress Dashboard",
    description: "See your emissions trends and achievements.",
    icon: <BarChart2 className="w-8 h-8 text-[#40e0d0] mb-2" />,
  },
];

const onboardingVariants = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -40, transition: { duration: 0.3 } },
};

const dashboardVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const featureVariants = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, x: -40, transition: { duration: 0.3 } },
};

const CarbonDashboard: React.FC<CarbonDashboardProps> = ({ open, onClose }) => {
  const [onboarding, setOnboarding] = useState(true);
  const [activeFeature, setActiveFeature] = useState<Feature>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };

    if (open) {
      getUser();
    }
  }, [open]);

  if (!open) return null;

  let featureComponent = null;
  if (activeFeature === "log") featureComponent = <DailyLog />;
  if (activeFeature === "calculator") featureComponent = <QuickCalculator />;
  if (activeFeature === "tips") featureComponent = <EcoTips />;
  if (activeFeature === "progress") featureComponent = <ProgressView />;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="dark:bg-[#1c1c1c] bg-white rounded-2xl p-8 shadow-2xl w-full max-w-3xl relative transition-all duration-500">
        <button
          className="absolute top-4 right-4 text-black dark:text-white text-xl"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <AnimatePresence mode="wait">
          {onboarding ? (
            <motion.div
              key="onboarding"
              variants={onboardingVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="flex flex-col items-center justify-center min-h-[400px]"
            >
              <div className="mb-6">
                {/* TODO: Replace with Lottie/SVG animation */}
                <span
                  role="img"
                  aria-label="leaf"
                  className="text-6xl animate-bounce"
                >
                  🌱
                </span>
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[#40e0d0]">
                Welcome to Your Carbon Tracker!
              </h2>
              <p className="text-lg text-muted-foreground mb-6 text-center max-w-md">
                See your impact, get eco tips, and track your progress toward a
                greener lifestyle.
              </p>
              <button
                className="px-8 py-3 rounded-xl bg-[#40e0d0] text-black font-bold text-lg shadow hover:bg-[#388e3c] transition"
                onClick={() => setOnboarding(false)}
              >
                Let's Go!
              </button>
            </motion.div>
          ) : activeFeature ? (
            <motion.div
              key={activeFeature}
              variants={featureVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <button
                className="flex items-center gap-2 mb-4 text-[#40e0d0] hover:underline"
                onClick={() => setActiveFeature(null)}
              >
                <ArrowLeft className="w-5 h-5" /> Back to Dashboard
              </button>
              {featureComponent}
            </motion.div>
          ) : (
            <motion.div
              key="dashboard"
              variants={dashboardVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <h2 className="text-3xl font-bold mb-8 text-[#40e0d0] text-center">
                Carbon Footprint Tracker
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {featureCards.map((card) => (
                  <button
                    key={card.key}
                    className="p-6 rounded-xl bg-[#f3f4f6] dark:bg-[#23272a] shadow flex flex-col items-center hover:scale-105 transition-transform focus:outline-none"
                    onClick={() => setActiveFeature(card.key as Feature)}
                  >
                    {card.icon}
                    <h3 className="font-semibold text-lg mb-2 text-[#222] dark:text-[#e6f4ea]">
                      {card.title}
                    </h3>
                    <p className="text-muted-foreground text-center">
                      {card.description}
                    </p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CarbonDashboard;
